import React, { useState, type ReactNode } from 'react';

import { FormControlLabel, Checkbox as MuiCheckbox } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

import type { BackgroundThemes } from 'shared/const';
import theme from 'shared/themes/default';

import IconUnchecked from './components/IconUnchecked';

interface CheckboxProps {
  attention?: boolean;
  checked?: boolean;
  disabled?: boolean;
  label?: string | ReactNode;
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  partialCheck?: boolean;
  theme?: BackgroundThemes;
}

const Checkbox = ({
  attention,
  checked,
  disabled,
  label,
  name,
  onChange,
  partialCheck,
  theme: themeLayout = 'dark',
  ...props
}: CheckboxProps) => {
  const [focus, setFocus] = useState(false);

  const getCheckedColor = () => {
    if (themeLayout === 'light') {
      return theme.palette.darkDistinct;
    }
    return attention ? theme.palette.brand : theme.palette.lightPure;
  };

  const sxGenericFormLabel: SxProps<Theme> = {
    color:
      themeLayout === 'light'
        ? theme.palette.mainDark
        : theme.palette.lightPure,
    display: 'flex',
    alignItems: 'start',
    '.MuiFormControlLabel-label': {
      width: '100%',
      marginTop: '0.6rem',
      '&.Mui-disabled': {
        color:
          themeLayout === 'light'
            ? theme.palette.disabledLight
            : theme.palette.disabledDark,
      },
    },
  };

  const sxGenericCheckbox: SxProps<Theme> = {
    color:
      themeLayout === 'light'
        ? theme.palette.darkDistinct
        : theme.palette.lightPure,
    marginRight: theme.spacing.xs,
    svg: {
      borderRadius: '3px',
      boxShadow: focus ? `0px 0px 0px 2px ${theme.palette.focus}` : 'unset',
      '.IconUncheckedStroke': {
        fill:
          themeLayout === 'light'
            ? theme.palette.darkDistinct
            : theme.palette.lightPure,
      },
      '.IconUncheckedBackground': {
        fill: 'transparent',
      },
    },
    '&.Mui-checked': {
      color: getCheckedColor(),
    },
    '&.Mui-disabled': {
      '&.Mui-checked': {
        color:
          themeLayout === 'light'
            ? theme.palette.disabledLight
            : theme.palette.disabledDark,
      },
      svg: {
        '.IconUncheckedStroke': {
          fill:
            themeLayout === 'light'
              ? theme.palette.disabledLight
              : theme.palette.disabledDark,
        },
      },
    },
    '&:hover': {
      svg: {
        '.IconUncheckedBackground': {
          fill:
            themeLayout === 'light'
              ? theme.palette.lightInteracting
              : theme.palette.darkWrapper,
        },
      },
    },
  };

  return (
    <FormControlLabel
      sx={sxGenericFormLabel}
      control={
        <MuiCheckbox
          checked={checked}
          disabled={disabled}
          disableRipple
          icon={<IconUnchecked />}
          indeterminate={checked && partialCheck}
          name={name}
          onChange={onChange}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          sx={sxGenericCheckbox}
          {...props}
        />
      }
      label={label}
      required={false}
    />
  );
};

export default Checkbox;
export type { CheckboxProps };
